/**
 * 上传中心 localStorage 持久化层。
 *
 * 设计取舍：
 * - 仅持久化「已结束」的批次（success / failed / cancelled）和未发起的 pending；
 *   uploading 状态不持久化（页面刷新时正在跑的 xhr 已被中断）。
 * - 持久化的目的：用户离开知识库 Tab、切到其他页面、甚至刷新后，
 *   仍能看到「上一次上传完成了多少条」的历史小结。
 * - 容量保护：最多保留 50 条，超出按 createdAt 升序裁剪。
 */

import type { PersistedUploadBatch, UploadBatch } from './types';

const STORAGE_KEY = 'manclaw.uploadCenter.batches.v1';
const MAX_PERSISTED = 50;

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/** 加载所有持久化批次。损坏数据会被静默重置。 */
export function loadBatches(): PersistedUploadBatch[] {
  if (!isBrowser()) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isValidBatch);
  } catch {
    // 损坏的数据：清掉，避免下次继续报错
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return [];
  }
}

/** 整体覆盖式保存（uploading 状态会被剔除，避免误展示为「正在上传」） */
export function saveBatches(batches: UploadBatch[]): void {
  if (!isBrowser()) {
    return;
  }
  const sanitized = batches
    .filter((b) => b.status !== 'uploading')
    .slice()
    .sort((a, b) => a.createdAt - b.createdAt)
    .slice(-MAX_PERSISTED);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
  } catch {
    // QuotaExceededError 等异常静默：上传功能本身不能因为持久化失败而中断
  }
}

/** 清空持久化历史（用户在浮窗里点「清空已完成」时调用） */
export function clearBatches(): void {
  if (!isBrowser()) {
    return;
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function isValidBatch(value: unknown): value is PersistedUploadBatch {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.projectKey === 'string' &&
    typeof v.assetType === 'number' &&
    typeof v.fileCount === 'number' &&
    typeof v.totalBytes === 'number' &&
    typeof v.progress === 'number' &&
    typeof v.status === 'string' &&
    typeof v.createdAt === 'number'
  );
}
